import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import * as pdfjs from "pdfjs-dist";
import { Button, Spin, message, Checkbox, Modal, Tooltip } from "antd";
import { useSelector } from "react-redux";
import { updateContentProgress, markContentAsCompleted } from "../../../service/UserCourseService";
import axios from "axios";
import "./UserPDFReaderModal.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;

const UserPDFReaderModal = forwardRef(({
  isOpen,
  fileName,
  onClose,
  contentId,
  courseId,
  blockTime,
  handleClose,
  isAcknowledged,
}, ref) => {
  const canvasRef = useRef(null);
  const lastPageRef = useRef(1);
  const containerRef = useRef(null);
  const fullscreenTimeoutRef = useRef(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [remainingTime, setRemainingTime] = useState(blockTime);
  const [timerActive, setTimerActive] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState(null);
  const [pdfFullyLoaded, setPdfFullyLoaded] = useState(false);
  const [isExitingFullscreen, setIsExitingFullscreen] = useState(false);
  const [hasReachedLastPage, setHasReachedLastPage] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closeConfirmType, setCloseConfirmType] = useState('');
  const [nextTimer, setNextTimer] = useState(10);
  const [pageTimers, setPageTimers] = useState({});
  const [nextTimerActive, setNextTimerActive] = useState(true);

  const storedUserId = localStorage.getItem("userId");
  const userId = useSelector((state) => state.auth.userId) || storedUserId;

  let activeRenderTask = null;

  const getCleanFilename = (fullFilename) => {
    if (!fullFilename) return '';
    const underscoreIndex = fullFilename.indexOf('_');
    return underscoreIndex === -1 ? fullFilename : fullFilename.substring(underscoreIndex + 1);
  };

  const displayName = getCleanFilename(fileName);

  useImperativeHandle(ref, () => ({
    saveProgress: () => {
       if (isAcknowledged) return;
      if (numPages && currentPage) {
        let completionPercentage = (currentPage / numPages) * 100;
        if (completionPercentage == 100 || numPages - currentPage == 0) {
          completionPercentage = 100;
        }
        try {
          updateContentProgress(userId, contentId, courseId, currentPage, completionPercentage, "pdf");
        } catch (error) {
          console.error("Error saving PDF progress:", error);
        }
      }
    }
  }));

  // Reset states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setRemainingTime(blockTime);
      setTimerActive(false);
      setPdfFullyLoaded(false);
      setError(null);
      setIsExitingFullscreen(false);
      setHasReachedLastPage(false);
      setHasAgreed(isAcknowledged || false);
    } else {
      setTimerActive(false);
      setPdfFullyLoaded(false);
      setIsExitingFullscreen(false);
      if (fullscreenTimeoutRef.current) clearTimeout(fullscreenTimeoutRef.current);
    }
  }, [isOpen, blockTime, isAcknowledged]);

  // Start timer only when PDF is fully loaded
  useEffect(() => {
    if (isOpen && pdfFullyLoaded && !error) {
      if (isAcknowledged) {
        setNextTimer(0);
        setNextTimerActive(false);
      } else if (!pageTimers[currentPage]) {
        setNextTimer(10);
        setNextTimerActive(true);
      } else {
        setNextTimer(0);
        setNextTimerActive(false);
      }
      setTimerActive(true);
    } else {
      setTimerActive(false);
    }
  }, [isOpen, pdfFullyLoaded, error, currentPage, pageTimers, isAcknowledged]);

  // Next page countdown logic
  useEffect(() => {
    let timer;
    if (nextTimerActive && nextTimer > 0) {
      timer = setInterval(() => {
        setNextTimer((prev) => Math.max(0, prev - 1));
      }, 1000);
    } else if (nextTimer === 0 && nextTimerActive) {
      setNextTimerActive(false);
      setPageTimers((prev) => ({ ...prev, [currentPage]: true }));
    }
    return () => clearInterval(timer);
  }, [nextTimerActive, nextTimer, currentPage]);

  // Fullscreen handling
  useEffect(() => {
    const handleFullScreenChange = () => {
      const isNowFullScreen = !!document.fullscreenElement;
      if (isFullScreen && !isNowFullScreen) {
        setIsExitingFullscreen(true);
        fullscreenTimeoutRef.current = setTimeout(() => {
          setIsExitingFullscreen(false);
          setIsFullScreen(false);
          if (pdfBlobUrl && currentPage) {
            setTimeout(() => loadPDF(pdfBlobUrl), 100);
          }
        }, 300);
      } else {
        setIsFullScreen(isNowFullScreen);
        setIsExitingFullscreen(false);
      }
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
      if (fullscreenTimeoutRef.current) clearTimeout(fullscreenTimeoutRef.current);
    };
  }, [isFullScreen, pdfBlobUrl, currentPage]);

  const toggleFullScreen = async () => {
    try {
      const wrapper = containerRef.current;
      if (!document.fullscreenElement && wrapper) {
        await wrapper.requestFullscreen();
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error("Fullscreen error:", err);
      message.error("Fullscreen mode not supported");
    }
  };

  // Fetch PDF from S3
  useEffect(() => {
    const fetchPdfFromS3 = async () => {
      if (!fileName || !isOpen) return;

      setLoading(true);
      setLoadingProgress(0);
      setError(null);
      setPdfFullyLoaded(false);

      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }

      try {
        const streamingUrl = `${process.env.REACT_APP_API_BASE_URL}/course/api/client-api/streaming/pdf/${fileName}`;
        const response = await axios.get(streamingUrl, {
          responseType: "blob",
          headers: { Accept: "application/pdf", "Cache-Control": "no-cache" },
          timeout: 60000,
          withCredentials: true,
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              setLoadingProgress(progress);
            }
          },
        });

        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(pdfBlob);
        setPdfBlobUrl(blobUrl);
        setLoadingProgress(100);
      } catch (err) {
        let errorMessage = "Failed to load PDF file.";
        if (err.code === "ECONNABORTED") errorMessage = "Request timeout.";
        else if (err.response?.status === 404) errorMessage = "PDF file not found.";
        setError(errorMessage);
        message.error(errorMessage);
        setPdfFullyLoaded(false);
      } finally {
        setLoading(false);
        setLoadingProgress(0);
      }
    };

    fetchPdfFromS3();
  }, [fileName, isOpen]);

  // Cleanup blob URL
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  // Pause timer on tab hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (pdfFullyLoaded && !isExitingFullscreen) {
        setTimerActive(document.visibilityState === "visible");
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [pdfFullyLoaded, isExitingFullscreen]);

  // Timer countdown
  useEffect(() => {
    let timer;
    if (timerActive && remainingTime > 0 && !isExitingFullscreen) {
      timer = setInterval(() => {
        setRemainingTime((prev) => Math.max(0, prev - 1));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [timerActive, remainingTime, isExitingFullscreen]);

  const updateProgress = (pageNumber) => {
    if (isAcknowledged) return Promise.resolve();
    if (pageNumber > 0 && numPages > 0 && userId && contentId && courseId) {
      let completionPercentage = (pageNumber / numPages) * 100;
      if (pageNumber === numPages || completionPercentage >= 95) completionPercentage = 100;
      return updateContentProgress(userId, contentId, courseId, pageNumber, completionPercentage, "pdf");
    }
    return Promise.resolve();
  };

  const loadPDF = async (blobUrl) => {
    try {
      if (!blobUrl) return;
      const loadingTask = pdfjs.getDocument({ url: blobUrl });
      const pdf = await loadingTask.promise;
      setNumPages(pdf.numPages);
      await renderPage(pdf, currentPage);
      setPdfFullyLoaded(true);
    } catch (error) {
      setError("Failed to render PDF document");
      setPdfFullyLoaded(false);
    }
  };

  const renderPage = async (pdf, pageNumber) => {
    try {
      const page = await pdf.getPage(pageNumber);
      let modalWidth;
      if (isFullScreen) {
        modalWidth = window.innerWidth * 0.95;
      } else {
        const container = containerRef.current;
        modalWidth = container ? container.clientWidth * 0.8 : window.innerWidth * 0.6;
      }

      const viewport = page.getViewport({
        scale: modalWidth / page.getViewport({ scale: 1 }).width,
      });

      const canvas = canvasRef.current;
      if (!canvas) return;
      const context = canvas.getContext("2d");

      if (activeRenderTask) activeRenderTask.cancel();
      context.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      activeRenderTask = page.render({ canvasContext: context, viewport });
      await activeRenderTask.promise;
      activeRenderTask = null;

      if (pageNumber === numPages) setHasReachedLastPage(true);
    } catch (error) {
      if (error.name !== 'RenderingCancelledException') {
        console.error("Error rendering page:", error);
      }
    }
  };

  useEffect(() => {
    if (pdfBlobUrl && !loading) loadPDF(pdfBlobUrl);
  }, [pdfBlobUrl, loading]);

  useEffect(() => {
    if (numPages && pdfBlobUrl && !loading) loadPDF(pdfBlobUrl);
  }, [currentPage]);

  const nextPage = () => {
    if (currentPage < numPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleModalClose = async () => {
    if (isExitingFullscreen) return;

    if (!isAcknowledged) {
      setCloseConfirmType(!hasAgreed ? 'no-agreement' : 'general');
    } else {
      setCloseConfirmType('acknowledged');
    }
    setShowCloseConfirm(true);
  };

  const handleConfirmClose = async () => {
    setShowCloseConfirm(false);

    if (numPages && currentPage) {
      let completionPercentage = (currentPage / numPages) * 100;
      if (completionPercentage >= 95 || currentPage === numPages) completionPercentage = 100;

      try {
        await updateProgress(currentPage);
        await markContentAsCompleted(courseId, contentId, userId);
        message.success("Course content completed successfully!");
        const closeModal = handleClose || onClose;
        if (typeof closeModal === 'function') closeModal();
      } catch (error) {
        const errorMsg = error?.response?.data?.message || "Failed to submit progress. Please check your connection and try again.";
        message.error(errorMsg);
      } finally {
        setIsSubmitting(false);
      }

      try {
        await updateContentProgress(userId, contentId, courseId, currentPage, completionPercentage, "pdf");
      } catch (error) {
        console.error("Error saving PDF progress:", error);
      }
    }

    if (handleClose) handleClose();
    else onClose();
  };

  const handleCancelClose = () => setShowCloseConfirm(false);

  const retryLoading = () => {
    setError(null);
    setPdfBlobUrl(null);
    setLoading(false);
    setPdfFullyLoaded(false);
  };

  const handleAgreement = () => setHasAgreed(true);

  const handleFinalSubmit = async (currentPage) => {
    if (!hasAgreed) {
      Modal.warning({
        title: "Terms and Conditions Required",
        content: "You must acknowledge the content before submitting. Please check the agreement box to proceed.",
        okText: "OK",
        getContainer: () => containerRef.current,
        zIndex: 1050,
      });
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await updateProgress(currentPage);
      await markContentAsCompleted(courseId, contentId, userId);
      message.success("Course content completed successfully!");
      const closeModal = handleClose || onClose;
      if (typeof closeModal === 'function') closeModal();
    } catch (error) {
      console.error("Submission failed:", { context: "UserPDFReaderModal/handleFinalSubmit", courseId, contentId, error });
      const errorMsg = error?.response?.data?.message || "Failed to submit progress. Please check your connection and try again.";
      message.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCloseDisabled = isAcknowledged
    ? isExitingFullscreen
    : (remainingTime > 0 || isExitingFullscreen);

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      className="show-content-container"
      style={{
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: isExitingFullscreen ? 'none' : 'auto'
      }}
    >
      {/* PDF Controls Header */}
      <div className="pdf-controls-header">
        <div className="pdf-title">
          {loading ? "Loading PDF..." : error ? "Error loading PDF" : !pdfFullyLoaded ? "Preparing PDF..." : (
            <>
              <div style={{ fontWeight: 'bold' }}>{displayName}</div>
              {remainingTime > 0 && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                  You can close in {remainingTime} second{remainingTime !== 1 ? "s" : ""}
                </div>
              )}
            </>
          )}
        </div>

        {!isFullScreen && (
          <div className="pdf-controls">
            <Button
              onClick={toggleFullScreen}
              className="pdf-control-btn"
              disabled={loading || error || !pdfFullyLoaded || isExitingFullscreen}
            >
              {isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
            </Button>
            <Button
              onClick={handleModalClose}
              disabled={isCloseDisabled}
              className="pdf-close-btn"
              style={{
                opacity: isCloseDisabled ? 0.5 : 1,
                cursor: isCloseDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              Close {remainingTime > 0 && `(${remainingTime}s)`}
            </Button>
          </div>
        )}
      </div>

      {/* PDF Viewer Content */}
      <div className="pdf-viewer-content">
        {loading ? (
          <div className="pdf-loading-state">
            <Spin tip={`Loading PDF... ${loadingProgress > 0 ? loadingProgress + '%' : ''}`} size="large" />
            <div className="loading-text">
              Loading {displayName}
              {loadingProgress > 0 && (
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div className="progress-bar-fill" style={{ width: `${loadingProgress}%` }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : error ? (
          <div className="pdf-error-state">
            <div>❌ Failed to load PDF</div>
            <div style={{ marginTop: 8, fontSize: "14px" }}>{error}</div>
            <div style={{ marginTop: 8, fontSize: "12px", opacity: 0.7 }}>File: {displayName}</div>
            <Button onClick={retryLoading} type="primary" className="retry-btn" style={{ marginTop: 16 }}>
              Retry Loading
            </Button>
          </div>
        ) : !pdfBlobUrl ? (
          <div className="pdf-error-state">
            <div>No PDF loaded</div>
            <div style={{ marginTop: 8, fontSize: "12px", opacity: 0.7 }}>File: {displayName}</div>
          </div>
        ) : (
          <div className="pdf-viewer-wrapper">
            <canvas ref={canvasRef} />
          </div>
        )}
      </div>

      {/* Agreement Checkbox */}
      {!isAcknowledged && numPages > 0 && currentPage === numPages && !nextTimerActive && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '10px 0',
          backgroundColor: '#f5f5f5',
          borderTop: '1px solid #d9d9d9',
          marginTop: '10px'
        }}>
          <Checkbox
            checked={hasAgreed}
            onChange={(e) => { if (e.target.checked) handleAgreement(); }}
            style={{ fontSize: '14px', fontWeight: '500' }}
          >
            I have read and agree to the above terms
          </Checkbox>
        </div>
      )}

      {/* Page Navigation */}
      {!loading && !error && numPages && (
        <div
          className="pdf-controls-header"
          style={{
            opacity: isExitingFullscreen ? 0.5 : 1,
            pointerEvents: isExitingFullscreen ? 'none' : 'auto'
          }}
        >
          <Button
            onClick={prevPage}
            disabled={currentPage === 1 || isExitingFullscreen}
            size="small"
            className="pdf-control-btn"
          >
            ⬅ Previous
          </Button>

          <div className="pdf-title">
            Page {currentPage} of {numPages}
            {numPages > 0 && (
              <span style={{ fontSize: "12px", opacity: 0.7, marginLeft: "8px" }}>
                ({Math.round((currentPage / numPages) * 100)}% complete)
              </span>
            )}
          </div>

          <Tooltip
            title={
              nextTimerActive
                ? `Please read this document until the timer ends (${nextTimer}s remaining).`
                : currentPage === numPages
                  ? "You are on the last page."
                  : "Proceed to next page."
            }
            placement="top"
          >
            <span>
              <Button
                onClick={nextPage}
                disabled={nextTimerActive || currentPage === numPages || isExitingFullscreen}
                size="small"
                className="pdf-control-btn"
              >
                {nextTimerActive ? `Next ➡ (${nextTimer}s)` : "Next ➡"}
              </Button>

              <Button
                onClick={() => handleFinalSubmit(currentPage)}
                disabled={currentPage !== numPages || isExitingFullscreen}
                size="small"
                className="pdf-control-btn"
                style={{ marginLeft: "10px" }}
              >
                Submit
              </Button>
            </span>
          </Tooltip>
        </div>
      )}

      {/* Close Confirmation Modal */}
      <Modal
  title={
    <span style={{ color: '#910E11', fontWeight: 600 , paddingLeft:'20px', paddingTop: '8px'}}>
      {closeConfirmType === 'no-agreement' ? '⚠️ Confirm Close' : 'Confirm Close'}
    </span>
  }
  open={showCloseConfirm}
  onOk={handleConfirmClose}
  onCancel={handleCancelClose}
  okText="Yes, Close"
  cancelText="Cancel"
  centered
  className="quiz-confirm-modal"
  zIndex={1000}
  styles={{ body: { padding: '20px' } }}
  okButtonProps={{
    danger: closeConfirmType === 'no-agreement',
    ...(closeConfirmType !== 'no-agreement' && {
      style: { backgroundColor: '#910E11', borderColor: '#910E11' }
    })
  }}
  cancelButtonProps={{
    style: { borderColor: '#910E11', color: '#910E11' }
  }}
>
  {closeConfirmType === 'no-agreement' && (
    <div style={{
      backgroundColor: '#fff7e6',
      border: '1px solid #ffd591',
      borderRadius: '8px',
      padding: '12px 16px',
    }}>
      <p style={{ color: '#faad14', marginBottom: '8px', fontWeight: 600 }}>
        ⚠️ You haven't completed the course.
      </p>
      <p style={{ margin: 0, color: '#444' }}>Are you sure you want to close?</p>
    </div>
  )}
  {(closeConfirmType === 'general' || closeConfirmType === 'acknowledged') && (
    <div style={{
      backgroundColor: '#fdf2f2',
      border: '1px solid #f5c6c6',
      borderRadius: '8px',
      padding: '12px 16px',
    }}>
      <p style={{ margin: 0, color: '#444' }}>Are you sure you want to close this document?</p>
    </div>
  )}
</Modal>
    </div>
  );
});

export default UserPDFReaderModal;